import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Button from '../components/UI/Button';

const ServicesScreen = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeService, setActiveService] = useState(0);

  const services = [
    {
      id: 0,
      title: 'Demi-pension',
      subtitle: t('services.custom.halfBoard.subtitle', 'Meals included: breakfast + dinner'),
      description: t('services.custom.halfBoard.description', 'Book for a defined number of days during your stay.'),
      image: '/assets/images/villa Baidane-38.jpg',
      features: t('services.custom.halfBoard.features', { returnObjects: true }) || [],
      pricing: t('services.custom.halfBoard.pricing', 'Contact us for pricing')
    },
    {
      id: 1,
      title: 'Pension complète',
      subtitle: t('services.custom.fullBoard.subtitle', 'Meals included: breakfast + lunch + dinner'),
      description: t('services.custom.fullBoard.description', 'Book for a defined number of days during your stay.'),
      image: '/assets/images/villa Baidane-35.jpg',
      features: t('services.custom.fullBoard.features', { returnObjects: true }) || [],
      pricing: t('services.custom.fullBoard.pricing', 'Contact us for pricing')
    },
    {
      id: 2,
      title: 'Location de voiture',
      subtitle: t('services.custom.car.subtitle', 'Pay directly to the provider'),
      description: t('services.custom.car.description', 'We will share provider contact details upon booking confirmation.'),
      image: '/assets/images/villa Baidane-01.jpg',
      features: t('services.custom.car.features', { returnObjects: true }) || [],
      pricing: t('services.custom.car.pricing', 'Prices available; to be paid directly to provider')
    },
    {
      id: 3,
      title: 'Massage',
      subtitle: t('services.custom.massage.subtitle', 'Pay directly to the provider'),
      description: t('services.custom.massage.description', 'We will share provider contact details upon booking confirmation.'),
      image: '/assets/images/villa Baidane-40.jpg',
      features: t('services.custom.massage.features', { returnObjects: true }) || [],
      pricing: t('services.custom.massage.pricing', 'Prices available; to be paid directly to provider')
    },
    {
      id: 4,
      title: 'Transfert',
      subtitle: t('services.custom.transfer.subtitle', 'Pay directly to the provider'),
      description: t('services.custom.transfer.description', 'We will share provider contact details upon booking confirmation.'),
      image: '/assets/images/villa Baidane-42.jpg',
      features: t('services.custom.transfer.features', { returnObjects: true }) || [],
      pricing: t('services.custom.transfer.pricing', 'Prices available; to be paid directly to provider')
    }
  ];

  return (
    <div className="min-h-screen  ">
      {/* Hero Section with Modern Gradient */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 bg-black bg-opacity-60"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat mix-blend-overlay"
          style={{
            backgroundImage: `url('/assets/images/villa Baidane-35.jpg')`
          }}
        ></div>
        
        {/* Floating Elements */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-accent/20 rounded-full animate-float"></div>
        <div className="absolute bottom-32 right-16 w-16 h-16 bg-white/10 rounded-full animate-float" style={{animationDelay: '1s'}}></div>
        <div className="absolute top-1/3 right-1/4 w-12 h-12 bg-accent/30 rounded-full animate-float" style={{animationDelay: '2s'}}></div>

        <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
          <h1 className="text-6xl md:text-8xl font-sofia font-bold mb-6 animate-fade-in-up">
            {t('services.title')}
          </h1>
          <p className="text-xl md:text-2xl font-sofia font-light mb-12 animate-fade-in-up opacity-90" style={{animationDelay: '0.2s'}}>
            {t('services.subtitle')}
          </p>
          
          {/* Modern CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up" style={{animationDelay: '0.4s'}}>
            <Button
              size="lg"
              className="bg-[#81736d] hover:bg-[#81736d]"
              onClick={() => {
                document.getElementById('services-section')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              {t('services.exploreServices')}
            </Button>
            <Button
               variant="outline"
              size="lg"
              className="border-white text-white hover:bg-[#81736d] hover:text-white"
              onClick={() => navigate('/contact')}
            >
              {t('services.contactUs')}
            </Button>
          </div>
        </div>

        {/* Modern Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-white animate-bounce">
          <div className="flex flex-col items-center">
            <span className="text-sm font-sofia mb-2 opacity-75">{t('services.scrollToExplore')}</span>
            <div className="w-6 h-10 border-2 border-white rounded-full flex justify-center">
              <div className="w-1 h-3 bg-white rounded-full mt-2 animate-pulse"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Services Section */}
      <section id="services-section" className="py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-16 animate-fade-in-up">
            <h2 className="text-5xl font-sofia font-bold text-black mb-6">
              {t('services.curatedExperiences.title')}
            </h2>
            <p className="text-xl font-sofia text-text-secondary max-w-3xl mx-auto">
              {t('services.curatedExperiences.description')}
            </p>
          </div>

          {/* Service Navigation Tabs */}
          <div className="flex flex-wrap justify-center gap-4 mb-12 animate-fade-in-up" style={{animationDelay: '0.2s'}}>
            {services.map((service, index) => (
              <button
                key={service.id}
                onClick={() => setActiveService(index)}
                className={`px-6 py-3 rounded-full font-sofia font-medium transition-all duration-300 transform hover:scale-105 ${
                  activeService === index
                    ? 'bg-[#81736d] text-white shadow-glow'
                    : 'bg-white text-text-primary hover:bg-[#81736d] border border-gray-200'
                }`}
              >
                {service.title}
              </button>
            ))}
          </div>

          {/* Active Service Display */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center animate-fade-in-up" style={{animationDelay: '0.4s'}}>
            <div className="order-2 lg:order-1">
              <div className="inline-block bg-accent/10 text-[#81736d] px-4 py-2 rounded-full text-sm font-sofia font-medium mb-4">
                {services[activeService].subtitle}
              </div>
              <h3 className="text-4xl font-sofia font-bold text-black mb-6">
                {services[activeService].title}
              </h3>
              <p className="text-lg font-sofia text-text-secondary mb-8 leading-relaxed">
                {services[activeService].description}
              </p>
              
              <div className="space-y-3 mb-8">
                {Array.isArray(services[activeService].features) && services[activeService].features.map((feature, index) => (
                  <div key={index} className="flex items-center animate-slide-in-left" style={{animationDelay: `${index * 0.1}s`}}>
                    <div className="w-6 h-6 bg-[#81736d] rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="font-sofia text-text-primary">{feature}</span>
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-between mb-8">
                <div>
                  <div className="text-2xl font-sofia font-bold text-[#81736d]">
                    {services[activeService].pricing}
                  </div>
                  <div className="text-sm font-sofia text-text-light">
                    Contact us for detailed pricing
                  </div>
                </div>
              </div>

              <div className="flex gap-4">
                <Button
                  className="hover:scale-105 transform transition-all duration-300"
                  onClick={() => navigate('/booking')}
                >
                  {t('services.bookNow')}
                </Button>
                <Button
                  variant="outline"
                  className="hover:scale-105 transform transition-all duration-300"
                  onClick={() => navigate('/contact')}
                >
                  {t('services.learnMore')}
                </Button>
              </div>
            </div>

            <div className="order-1 lg:order-2">
              <div className="relative group">
                <img 
                  src={services[activeService].image}
                  alt={services[activeService].title}
                  className="w-full h-96 object-cover rounded-2xl shadow-large transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl"></div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesScreen;
